import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type { Integration, IntegrationBlock } from '@gitbook/api-client';
import { Button, CardButton, Description, Heading, ImageIcon, Tooltip } from '@gitbook/spine-react';

import { useNavigation, useNavigationState } from '../../../navigation';
import { apiQuery } from '../../../state';
import { IntegrationIcon } from '../..';
import { BlockLoadingFallback } from '../common';
import { ContentEditorContext } from '../contexts';
import { useIntegrationForURL } from './useIntegrationForURL';

/**
 * Render an embed.
 */
export function EmbedView(props: { url: string; readOnly: boolean }): React.ReactElement {
    const { url, readOnly } = props;

    const { data: embed } = useSuspenseQuery(
        apiQuery('getEmbedByUrl', { url }, { nullOnError: true })
    );

    // When editing, find if there are integrations in the marketplace that match this url
    // and suggest the user to install it
    const integrationMatch = useIntegrationForURL(readOnly ? null : url);
    const integrationButton = integrationMatch ? (
        <InstallIntegrationButton {...integrationMatch} />
    ) : null;

    const Icon = React.useMemo(
        () => (embed?.icon ? <ImageIcon size={32} src={embed.icon} /> : undefined),
        [embed?.icon]
    );

    if (!embed) {
        return <BlockLoadingFallback />;
    }

    if (embed.type === 'rich' && embed.html) {
        const rawHtml = (
            <div
                // biome-ignore lint/security/noDangerouslySetInnerHtml: This is a trusted source
                dangerouslySetInnerHTML={{
                    __html: embed.html,
                }}
            />
        );

        if (integrationButton) {
            return (
                <div className="relative">
                    {rawHtml}
                    <div className="absolute right-4 bottom-4">{integrationButton}</div>
                </div>
            );
        }
        return rawHtml;
    }

    return (
        <CardButton
            onPress={() => {
                window.open(url, '_blank');
            }}
            className="group flex items-center gap-4 p-4"
        >
            {Icon ? <div className="flex shrink-0">{Icon}</div> : null}
            <div className="flex flex-1 flex-col">
                <Heading className="group-hover:text-primary">{embed.title}</Heading>
                <Description>{embed.site}</Description>
            </div>
            {integrationButton ? <div className="ml-4 shrink-0">{integrationButton}</div> : null}
        </CardButton>
    );
}

/**
 * Button to suggest installing an integration.
 */
export function InstallIntegrationButton(props: {
    integration: Integration;
    block: IntegrationBlock;
}): React.ReactElement {
    const { integration } = props;
    const { navigate } = useNavigation();
    const { screen } = useNavigationState();
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);

    const onPress = () => {
        logAnalyticsEvent({
            type: 'edit_open_integration',
            integration: integration.name,
            reason: 'embed',
        });

        if (screen.type === 'space' || screen.type === 'site') {
            navigate({
                current: {
                    sideSheet: {
                        type: 'integrations',
                        view: { type: 'integration', integration: integration.name },
                    },
                },
            });
        } else {
            navigate({
                screen: {
                    type: 'integration',
                    integration: integration.name,
                },
            });
        }
    };

    return (
        <Tooltip label="Install this integration to supercharge this block">
            <Button
                kind="secondary"
                size="medium"
                leadingIcon={<IntegrationIcon integration={integration} />}
                onPress={onPress}
                label={`Install ${integration.title}`}
            />
        </Tooltip>
    );
}
